import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, Navigate } from 'react-router-dom';
import { CertificateRequestListRow } from '../../components/forms/CertificateRequestListRow';
import { PageHeader } from '../../components/ui/PageHeader';
import { PageToolbar } from '../../components/ui/PageToolbar';
import { PageNotificationToast } from '../../components/notifications/PageNotificationToast';
import { usePageNotifications } from '../../hooks/usePageNotifications';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import {
  certificateTypeLabels,
  fetchCertificateRequestSubmissions,
} from '../../lib/certificateRequestSubmissions';
import { canAccessCertificateRequest } from '../../lib/applicationFormWorkflow';
import { STATUS_LABEL } from '../../lib/accountCreationUi';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import { useFormsSubmissionsRealtime } from '../../hooks/useFormsSubmissionsRealtime';
import type { ApplicationSubmission, CertificateRequestFormData } from '../../types/applicationForm';

function ctrRowSearchBlob(row: ApplicationSubmission): string {
  const data = row.form_data as CertificateRequestFormData;
  const itemNos = (data?.items ?? []).map((item) => item.item_no).join(' ');
  return [
    row.application_no,
    row.company_name,
    data?.customer_no,
    data?.customer_name,
    data?.sales_code,
    data?.contact_person,
    data?.email,
    certificateTypeLabels(data),
    data?.type_others,
    itemNos,
    data?.requested_by,
    data?.prepared_by,
    data?.verified_by,
    row.submitted_by,
    row.status,
    STATUS_LABEL[row.status],
  ]
    .map((v) => String(v ?? '').toLowerCase())
    .join('\n');
}

export function CertificateRequestListPage() {
  const { basePath } = usePortal();
  const { portalAccess, loading: authLoading } = useAuth();
  const [rows, setRows] = useState<ApplicationSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const lastErrorToastRef = useRef<string | null>(null);
  const { push: pushToast, activeToast, dismissToast } = usePageNotifications();

  useEffect(() => {
    if (error && error !== lastErrorToastRef.current) {
      lastErrorToastRef.current = error;
      pushToast(pageFeedbackToToast({ kind: 'error', message: error }));
    }
    if (!error) lastErrorToastRef.current = null;
  }, [error, pushToast]);

  const homePath = basePath || '/forms';
  const listBase = basePath ? `${basePath}/certificate-request` : '/certificate-request';
  const newPath = `${listBase}/new`;

  const allowed = canAccessCertificateRequest({
    elevatedDepartment: portalAccess?.elevatedDepartment,
    departmentSlug: portalAccess?.departmentSlug,
    formsRole: portalAccess?.forms.profile?.role,
  });

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = Boolean(opts?.silent);
    if (!silent) setLoading(true);
    setError(null);
    try {
      setRows(await fetchCertificateRequestSubmissions());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load certificate requests.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !allowed) return;
    void load();
  }, [authLoading, allowed, load]);

  useFormsSubmissionsRealtime({
    enabled: !authLoading && allowed,
    tables: ['certificate_request_submissions'],
    onChange: () => {
      void load({ silent: true });
    },
  });

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;
    if (q) {
      const tokens = q.split(/\s+/).filter(Boolean);
      list = rows.filter((row) => {
        const blob = ctrRowSearchBlob(row);
        return tokens.every((token) => blob.includes(token));
      });
    }
    return [...list].sort((a, b) => b.submitted_at.localeCompare(a.submitted_at));
  }, [rows, search]);

  const stats = useMemo(() => {
    let pending = 0;
    let processing = 0;
    for (const row of rows) {
      if (row.status === 'new' || row.status === 'pending_approval' || row.status === 'saved') {
        pending += 1;
      }
      if (row.status === 'processing') processing += 1;
    }
    return { total: rows.length, pending, processing };
  }, [rows]);

  if (authLoading) {
    return (
      <div className="ctr-page">
        <p className="page-lead">Checking access…</p>
      </div>
    );
  }

  if (!allowed) {
    return <Navigate to={homePath} replace />;
  }

  const detailPath = (id: string) => `${listBase}/${id}`;
  const countLabel = loading
    ? 'Loading…'
    : search.trim()
      ? `${filteredRows.length} of ${rows.length} request${rows.length === 1 ? '' : 's'}`
      : `${filteredRows.length} request${filteredRows.length === 1 ? '' : 's'}`;

  return (
    <div className="ctr-page">
      <PageHeader
        title="Certificate Request"
        description="Certificate requests (PU-040) — requestor, authorised users and approvers."
        actions={
          <div className="ctr-page__header-actions">
            <Link to={homePath} className="btn-secondary">
              All forms
            </Link>
            <Link to={newPath} className="btn-primary">
              + New request
            </Link>
          </div>
        }
      />

      <div className="kpi-grid dashboard-kpis">
        <div className="card kpi-card">
          <div className="kpi-label">Total</div>
          <div className="kpi-value">{stats.total}</div>
        </div>
        <div className="card kpi-card">
          <div className="kpi-label">New / pending approval</div>
          <div className="kpi-value">{stats.pending}</div>
        </div>
        <div className="card kpi-card">
          <div className="kpi-label">Processing</div>
          <div className="kpi-value">{stats.processing}</div>
        </div>
      </div>

      <PageToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by ref, customer, type, item or prepared by"
        action={
          <div className="ctr-page__toolbar-aside">
            <span className="page-count">{countLabel}</span>
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={loading}
              onClick={() => void load()}
            >
              Refresh
            </button>
          </div>
        }
      />

      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}

      {loading && rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__spinner" />
          <p>Loading certificate requests…</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>No certificate requests yet.</p>
          <Link to={newPath} className="btn-primary btn-sm" style={{ marginTop: '0.75rem' }}>
            + New request
          </Link>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="empty-state">
          <p>No requests match your search.</p>
          <button type="button" className="btn-secondary btn-sm" onClick={() => setSearch('')}>
            Clear search
          </button>
        </div>
      ) : (
        <div className="ctr-list card">
          <div className="ctr-list__head" role="row">
            <span>Ref.</span>
            <span>Customer</span>
            <span>Type</span>
            <span>Items</span>
            <span>Prepared by</span>
            <span>Status</span>
            <span aria-hidden />
          </div>
          {filteredRows.map((row) => (
            <CertificateRequestListRow key={row.id} row={row} detailPath={detailPath(row.id)} />
          ))}
        </div>
      )}
    </div>
  );
}
